import * as React from 'react';
import type {ChatMessage} from 'src/components/agentic-ui';
import type {ApiPostOptions} from 'src/utils/redux-api';
import {useDispatch, useSelector} from 'react-redux';
import {
  ChatPanelLexical,
  type ChatPanelHandle,
} from 'src/components/agentic-ui/chat-panel';
import {
  selectFullscreen,
  selectSideNavExpanded,
} from 'src/selectors/app-config';
import {
  useRespondToAutoAttach,
  useRequestToAutoAttach,
  useHeaderSlot,
} from 'src/components/agentic-ui/use-auto-attach';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {classify} from '@spaced-out/ui-design-system/lib/utils/classify';
import {
  size260,
  size20,
  size60,
} from '@spaced-out/ui-design-system/lib/styles/variables/_size';
import {useApiMutation} from 'src/hooks/useApiMutation';
import {
  startScreens,
  DefaultStartScreen,
} from 'src/components/agentic-ui/start-screens';
import {useApiQuery} from 'src/hooks/useApiQuery';
import * as reduxApiAskAi from 'src/utils/redux-api-ask-ai';
import css from './ask-ai.css';
import {skipToken} from '@tanstack/react-query';
import {
  registerChatPanel,
  useAskAIProduct,
  useAskAIThreadId,
  setThreadId as setThreadIdInStore,
  clearThreadId as clearThreadIdInStore,
} from './ask-ai-store';
import {runPreSendCollectors} from './context-collectors';
import {publishAgenticEvent} from './agentic-ui-event-bus';
import {useHistory} from 'src/rerouter';
import {useAgencyApiHeaders} from 'src/components/agentic-ui';


const LAST_MODE_STORAGE_KEY = 'askai:chatMode';
const POSITION_PERSIST_KEY = 'askai:chatPos';

type ThreadCreatePayload = {
  name: string;
  context: {
    page_context: {
      product: string;
    };
  };
};

export const AskAI = () => {
  const {t} = useLabelTranslation();
  const chatPanelRef = React.useRef<ChatPanelHandle | null>(null);
  const dispatch = useDispatch();
  const {headerSlot} = useRespondToAutoAttach(chatPanelRef);
  const product = useAskAIProduct();
  const threadId = useAskAIThreadId();
  const history = useHistory();
  const agencyHeaders = useAgencyApiHeaders();
  const isFullscreenExperience = useSelector(selectFullscreen);
  const isSideNavExpanded = useSelector(selectSideNavExpanded);

  const panelLeftMargin = React.useMemo(() => {
    if (isFullscreenExperience) {
      return undefined; // fullscreen, no side nav, use default
    }

    return isSideNavExpanded
      ? parseInt(size260)
      : parseInt(size60) + parseInt(size20);
  }, [isFullscreenExperience, isSideNavExpanded]);

  const {data: threadData} = useApiQuery<{messages: ChatMessage[]}>(
    `conversation/${threadId}`,
    {
      queryOptions: {
        queryKey: threadId ? ['message-history', threadId] : skipToken,
        enabled: !!threadId,
      },
      apiOptions: {
        method: 'GET',
        options: {
          apiPath: '/grace/api/v1/',
          headers: agencyHeaders,
        },
      },
    },
  );
  const messageHistory = threadData?.messages || [];

  //if threadId is not there make an api call using useApiMutation to create a thread and set it in local storage
  const {mutateAsync: createThread, isPending: _isUpserting} = useApiMutation({
    mutationFn: async (payload: ThreadCreatePayload) => {
      return dispatch(
        reduxApiAskAi.post('conversation', payload, undefined, {
          headers: agencyHeaders,
        } as ApiPostOptions),
      );
    },
  });

  const handleCreateThread = async (): Promise<string> => {
    const thread = await createThread({
      name: 'Dummy thread name',
      context: {
        page_context: {
          product,
        },
      },
    });
    setThreadIdInStore(thread.id);
    return thread.id;
  };

  const handleClearThread = () => {
    clearThreadIdInStore();
  };

  const getStreamUrl = async () => {
    const id = threadId ?? (await handleCreateThread());

    return `/grace/api/v1/conversation/${id}/chat`;
  };

  return (
    <>
      <ChatPanelLexical
        ref={(node) => {
          chatPanelRef.current = node;
          registerChatPanel(node);
        }}
        attachChatTo="#chat-container"
        attachHeaderTo="#chat-header-container"
        defaultMode="hidden"
        placeholder={t('Ask_me_anything', 'Ask me anything')}
        lastModePersistKey={LAST_MODE_STORAGE_KEY}
        positionPersistKey={POSITION_PERSIST_KEY}
        renderStartScreen={({onSelectPrompt}) => {
          if (threadId) {
            return null;
          }
          const key = product;
          const Comp = startScreens[key] || DefaultStartScreen;
          return <Comp source={product} onSelectPrompt={onSelectPrompt} />;
        }}
        messageHistory={messageHistory}
        //we need this prop in attached mode to allow host to provide
        //custom content in the header slot
        headerSlot={headerSlot}
        height={400}
        chatOptions={{
          // resolve SSE URL lazily (creates thread on first send if needed)
          getStreamUrl,
          headers: {
            Authorization: `Bearer ${'test'}`,
            ...agencyHeaders,
          },
          collectMessageContext: () =>
            runPreSendCollectors({history}, undefined),
          publishMessageAction: (action: unknown) => {
            try {
              const {product, event, payload, dedupeKey} = (action || {}) as {
                product: unknown;
                event: unknown;
                payload?: unknown;
                dedupeKey?: string;
              };
              if (event) {
                const emit = publishAgenticEvent as unknown as (
                  product: unknown,
                  event: unknown,
                  payload: unknown,
                  dedupeKey?: string,
                ) => void;
                emit(product, event, payload ?? null, dedupeKey);
              }
            } catch {}
          },
          backoff: {
            baseMs: 1000,
            maxMs: 15000,
            maxAttempts: 1,
          },
        }}
        defaultOpen={true}
        onReset={handleClearThread}
        panelLeftMargin={panelLeftMargin}
        onClose={handleClearThread}
      />
    </>
  );
};

export interface AskAIChatHostProps {
  className?: string;
}

export const AskAIChatHost = ({className}: AskAIChatHostProps) => {
  useRequestToAutoAttach();
  return (
    <div
      id="chat-container"
      className={classify(css.panelWrapper, className)}
    ></div>
  );
};
export interface AskAIHeaderHostProps {
  className?: string;
  slot?: React.ReactNode;
}

export const AskAIHeaderHost = ({className, slot}: AskAIHeaderHostProps) => {
  useHeaderSlot(slot);
  return (
    <div
      id="chat-header-container"
      className={classify(css.headerWrapper, className)}
    ></div>
  );
};
